import type { PrinciplesCatalog, TrizState } from "../api/types";

/**
 * Сериализует TrizState в человекочитаемый markdown — для копирования в
 * Obsidian / Notion / любую заметочную систему. Покрывает финальный вывод,
 * противоречия, кандидатов, решения с оценками.
 */
export function stateToMarkdown(
  state: TrizState,
  principles: PrinciplesCatalog | undefined,
): string {
  const lines: string[] = [];

  const date = new Date(state.created_at);
  const dateStr = date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  lines.push(`# Прогон от ${dateStr}`);
  lines.push("");
  lines.push(`**Задача:**`);
  lines.push("");
  lines.push("> " + state.task.replace(/\n/g, "\n> "));
  lines.push("");

  if (state.conclusion) {
    const c = state.conclusion;
    const isFinal = c.confidence_level === "final";
    lines.push(`## ${isFinal ? "✅ Окончательный вывод" : "⏳ Промежуточный вывод"}`);
    lines.push("");
    lines.push(`**Решение #${c.top_solution_index + 1}.** ${c.action_summary}`);
    lines.push("");
    lines.push(`**Первый шаг:** ${c.first_step}`);
    lines.push("");
    if (c.alternatives.length > 0) {
      lines.push(`**Альтернативы:**`);
      for (const alt of c.alternatives) {
        lines.push(`- Решение #${alt.solution_index + 1} — ${alt.why_relevant}`);
      }
      lines.push("");
    }
    lines.push(`*Уверенность: ${c.confidence_reasoning}*`);
    if (!isFinal && c.missing_inputs.length > 0) {
      lines.push("");
      lines.push(`**Чтобы вывод стал окончательным — нужны уточнения:**`);
      for (const m of c.missing_inputs) lines.push(`- ${m}`);
    }
    lines.push("");
  }

  if (state.components.length > 0) {
    lines.push("## Компоненты системы");
    lines.push("");
    for (const c of state.components) {
      lines.push(`- **${c.name}** — ${c.role}`);
    }
    lines.push("");
  }

  if (state.technical_contradiction) {
    const tp = state.technical_contradiction;
    lines.push("## Техническое противоречие");
    lines.push("");
    lines.push(`- **Если:** ${tp.if_situation}`);
    lines.push(`- **То:** ${tp.then_benefit}`);
    lines.push(`- **Но:** ${tp.but_cost}`);
    if (tp.improving_param && tp.worsening_param) {
      lines.push(
        `- Параметры матрицы: ↑ №${tp.improving_param} / ↓ №${tp.worsening_param}`,
      );
    }
    lines.push("");
  }

  if (state.physical_contradiction) {
    const fp = state.physical_contradiction;
    lines.push("## Физическое противоречие");
    lines.push("");
    lines.push(`- **Элемент:** ${fp.element}`);
    lines.push(`- **Должен быть:** ${fp.requirement_a}`);
    lines.push(`  - причина: ${fp.rationale_a}`);
    lines.push(`- **И одновременно:** ${fp.requirement_not_a}`);
    lines.push(`  - причина: ${fp.rationale_not_a}`);
    lines.push("");
  }

  if (state.candidate_principles.length > 0) {
    lines.push("## Приёмы-кандидаты");
    lines.push("");
    for (const c of state.candidate_principles) {
      const name = principles?.[c.principle_id]?.name_ru ?? "";
      const header = name ? `№${c.principle_id} (${name})` : `№${c.principle_id}`;
      lines.push(`- **${header}** — ${c.rationale}`);
    }
    lines.push("");
  }

  if (state.solutions.length > 0) {
    lines.push("## Решения и оценки");
    lines.push("");
    for (let i = 0; i < state.solutions.length; i++) {
      const sol = state.solutions[i];
      const ev = state.evaluations[i] ?? null;
      const score = ev ? ev.score.toFixed(2) : "—";
      const principleNames = sol.principles_used.map((p) => {
        const name = principles?.[p]?.name_ru;
        return name ? `№${p} (${name})` : `№${p}`;
      });
      const principleStr = principleNames.join(", ") || "—";
      lines.push(`### Решение #${i + 1} · score ${score}`);
      lines.push("");
      lines.push(`*Приёмы: ${principleStr}*`);
      lines.push("");
      lines.push(sol.description);
      if (ev) {
        lines.push("");
        lines.push(`**Критик:** ${ev.rationale}`);
        if (ev.suggested_revisions) {
          lines.push("");
          lines.push(`**Поправка:** ${ev.suggested_revisions}`);
        }
      }
      lines.push("");
    }
  }

  return lines.join("\n");
}

export function downloadMarkdown(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
